/* eslint-disable */
/* eslint-disable no-undef */
import useVirtulization from '@/Helpers/useVirtulization';
import {
	ArrowDown, ArrowUp, ArrowUpDown, Loader2
} from 'lucide-react';
import { useMemo, useState, useRef, useCallback, useEffect } from 'react';

function VariantTable(props) {
	const {
		data = [],
		columns = [],
		colMapping,
		customRowData = {},
		sortValueInfo = {},
		customCol = () => { },
		highLightRow = null,
		virtualization = false,
		isLoading: externalLoading = false,
		deferRender = false,
		customInfoLine = 'No data available',
	} = props;

	const [sortColumn, setSortColumn] = useState(null);
	const [sortDirection, setSortDirection] = useState('asc');
	const [isScrolled, setIsScrolled] = useState(false);
	const [isInternalLoading, setIsInternalLoading] = useState(false);
	const [deferredData, setDeferredData] = useState(data);
	const [headerHeight, setHeaderHeight] = useState(0);
	const scrollContainerRef = useRef(null);
	const theadRef = useRef(null);
	const dataRef = useRef(data);

	useEffect(() => {
		const updateHeaderHeight = () => {
			if (theadRef.current) {
				requestAnimationFrame(() => {
					setHeaderHeight(theadRef.current.offsetHeight);
				});
			}
		};
		updateHeaderHeight();
		const resizeObserver = new ResizeObserver(updateHeaderHeight);
		if (theadRef.current) {
			resizeObserver.observe(theadRef.current);
		}
		return () => resizeObserver.disconnect();
	}, [columns]);

	useEffect(() => {
		if (!deferRender) {
			setDeferredData(data);
			return;
		}

		if (dataRef.current !== data) {
			dataRef.current = data;
			setIsInternalLoading(true);

			requestAnimationFrame(() => {
				setTimeout(() => {
					setDeferredData(data);
					requestAnimationFrame(() => {
						setIsInternalLoading(false);
					});
				}, 0);
			});
		}
	}, [data, deferRender]);

	const isLoading = externalLoading || isInternalLoading;
	const activeData = deferRender ? deferredData : data;

	const getSortIcon = (column) => {
		if (!column?.sortable) return null;

		if (sortColumn === column?.key) {
			return sortDirection === 'asc'
				? <ArrowUp size={20} className='cursor-pointer text-blue-600' />
				: <ArrowDown size={20} className='cursor-pointer text-blue-600' />;
		}
		return <ArrowUpDown size={20} className='cursor-pointer text-gray-400 hover:text-gray-600' />;
	};

	const handleSort = (column) => {
		if (!column?.sortable) return;
		setSortColumn(column?.key);
		setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
	};

	const getValueByPath = (obj, path) => {
		const cleanPath = path.replace(/obj\??\./g, '').split('.');
		return cleanPath.reduce((current, key) => current?.[key], obj);
	};

	const sortedData = useMemo(() => {
		if (!sortColumn) return activeData;

		// Separate fixed and non-fixed rows
		const fixedRows = activeData.filter(row => row?.fixed);
		const nonFixedRows = activeData.filter(row => !row?.fixed);

		// Sort only non-fixed rows
		const sortedNonFixed = [...nonFixedRows].sort((a, b) => {
			let aValue;
			let bValue;
			let sortType = 'string';

			if (sortValueInfo && sortValueInfo[sortColumn]) {
				const sortInfo = sortValueInfo[sortColumn];
				sortType = sortInfo?.type || 'string';
				if (sortInfo?.customValue) {
					aValue = typeof sortInfo?.value === 'function' ? sortInfo?.value(a) : a[sortInfo?.value];
					bValue = typeof sortInfo?.value === 'function' ? sortInfo?.value(b) : b[sortInfo?.value];
				} else {
					if (sortInfo?.path) {
						aValue = getValueByPath(a, sortInfo?.path);
						bValue = getValueByPath(b, sortInfo?.path);
					} else {
						aValue = a[sortInfo?.value];
						bValue = b[sortInfo?.value];
					}
				}
			} else {
				// Default sorting logic when no sortValueInfo is provided
				const dataKey = (colMapping && colMapping[sortColumn]) || sortColumn;
				aValue = a[dataKey];
				bValue = b[dataKey];
				if (typeof aValue === 'number' && typeof bValue === 'number') {
					sortType = 'number';
				}
			}

			if (sortType === 'number') {
				const aNum = parseFloat(aValue) || 0;
				const bNum = parseFloat(bValue) || 0;
				return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
			}

			const aStr = String(aValue || '').toLowerCase();
			const bStr = String(bValue || '').toLowerCase();
			return sortDirection === 'asc'
				? aStr.localeCompare(bStr)
				: bStr.localeCompare(aStr);
		});

		// Return fixed rows first, then sorted non-fixed rows
		return [...fixedRows, ...sortedNonFixed];
	}, [activeData, sortColumn, sortDirection, colMapping, customRowData, sortValueInfo]);

	const rowVirtualization = useVirtulization({
		data: sortedData,
		rowHeight: 50,
		scrollContainerRef,
		visibleCount: 10,
		overscan: 5,
		enabled: virtualization,
	});

	const renderCell = (product, column, index, absoluteIndex) => {
		return customRowData[column?.key]
			? customRowData[column?.key](product, index, column?.key, absoluteIndex) ?? '-'
			: product[colMapping[column?.key]];
	};

	const getFixedColumnOffset = (columnIndex) => {
		let offset = 0;
		for (let i = 0; i < columnIndex; i++) {
			if (columns[i]?.fixed) {
				offset += columns[i]?.width || 200;
			}
		}
		return offset;
	};

	const getLastFixedColumnIndex = () => {
		for (let i = columns.length - 1; i >= 0; i--) {
			if (columns[i]?.fixed) {
				return i;
			}
		}
		return -1;
	};

	const handleScroll = useCallback(() => {
		if (scrollContainerRef.current) {
			const { scrollLeft } = scrollContainerRef.current;
			setIsScrolled(scrollLeft > 0);
		}
	}, []);

	return (
		<div
			ref={scrollContainerRef}
			className='h-full w-full overflow-auto relative'
			onScroll={handleScroll}
		>
			{isLoading && (
				<div className='absolute inset-0 bg-white bg-opacity-70 flex items-center justify-center z-50'>
					<div className='flex flex-col items-center gap-2'>
						<Loader2 className='w-8 h-8 text-yellow-500 animate-spin' />
						<span className='text-sm text-gray-600'>Loading...</span>
					</div>
				</div>
			)}
			<table className='w-full'>
				<thead ref={theadRef} className='bg-gray-50'>
					<tr className='bg-yellow-200'>
						{columns.map((column, index) => {
							const isLastFixed = index === getLastFixedColumnIndex();
							const isFixedColumn = column.fixed;
							return (
								<th
									key={column.key}
									className={`py-3 gap-2 text-center px-1 text-sm bg-yellow-200 font-medium text-gray-700 border-b border-gray-300 sticky top-0 ${isFixedColumn ? 'z-50' : 'z-40'}`}
									style={{
										...(isFixedColumn ? { left: `${getFixedColumnOffset(index)}px` } : {}),
										width: column.width ? `${column.width}px` : '200px',
										minWidth: column.width ? `${column.width}px` : '200px',
									}}
									onClick={() => handleSort(column)}
								>
									<div className={`flex items-center justify-center gap-2 w-full`}>
										{column?.customCol ? customCol(column) : column.title}
										{column?.sortable && getSortIcon(column)}
									</div>
									{isLastFixed && isScrolled && (
										<div
											style={{
												position: 'absolute',
												top: 0,
												right: 0,
												width: '8px',
												height: '100%',
												pointerEvents: 'none',
												boxShadow: '4px 0 6px -2px rgb(243, 220, 16)',
												zIndex: 1
											}}
										/>
									)}
								</th>
							);
						})}
					</tr>
				</thead>
				<tbody className='divide-y divide-gray-200'>
					{virtualization && rowVirtualization.paddingTop > 0 && (
						<tr>
							<td colSpan={columns.length} style={{ height: rowVirtualization.paddingTop }} />
						</tr>
					)}
					{sortedData?.length > 0 ? (
						(virtualization ? rowVirtualization.virtualRows : sortedData).map((product, index) => {
							const absoluteIndex = virtualization
								? rowVirtualization.startIndex + index
								: index;
							const isFixedRow = product?.fixed;
							return (
								<tr
									key={product.id || index}
									className={`hover:bg-gray-50 ${highLightRow == index ? 'bg-yellow-100' : ''} ${isFixedRow ? 'sticky z-20 bg-yellow-500' : ''}`}
									style={isFixedRow ? { top: `${headerHeight}px` } : {}}
								>
									{columns?.map((column, colIndex) => {
										const isLastFixed = colIndex === getLastFixedColumnIndex();
										const isFixedColumn = column?.fixed;
										const isColoured = column?.coloured;
										return (
											<td
												key={column.key}
												className={`text-left ${column.noPadding ? 'p-0' : 'px-4 py-3'} text-sm border-b border-gray-200 ${isFixedColumn
													? `sticky ${isColoured ? 'bg-slate-100' : 'bg-white'} ${isFixedRow ? 'bg-yellow-500' : ''}`
													: ''
													} ${isColoured ? 'bg-slate-100' : ''} ${isFixedRow ? 'bg-yellow-500' : ''}`}
												style={{
													...(isFixedColumn ? { left: `${getFixedColumnOffset(colIndex)}px` } : {}),
													width: column.width ? `${column.width}px` : '200px',
													minWidth: column.width ? `${column.width}px` : '200px',
													zIndex: isFixedColumn ? 10 : 1,
												}}
											>
												{renderCell(product, column, index, absoluteIndex)}
												{isLastFixed && isScrolled && (
													<div
														style={{
															position: 'absolute',
															top: 0,
															right: 0,
															width: '8px',
															height: '100%',
															pointerEvents: 'none',
															boxShadow: '4px 0 6px -2px rgb(243, 220, 16)',
															zIndex: 1
														}}
													/>
												)}
											</td>
										);
									})}
								</tr>
							);
						})
					) : (
						<tr>
							<td
								colSpan={columns.length}
								className='px-4 py-6 text-center text-sm text-gray-500'
							>
								{customInfoLine}
							</td>
						</tr>
					)}
					{virtualization && rowVirtualization.paddingBottom > 0 && (
						<tr>
							<td colSpan={columns.length} style={{ height: rowVirtualization.paddingBottom }} />
						</tr>
					)}
				</tbody>
			</table>
		</div>
	);
}

export default VariantTable;